import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import { avatarSrc } from "../game/avatar";
import { games } from "./games";
import type { PublicTable } from "../gen/bazivan/v1/table_pb";
import { BotIcon } from "./BotIcon";
import { placeLabel } from "../game/place";

/**
 * فهرستِ میزهای باز — «ببین بقیه چطور بازی می‌کنند».
 *
 * فقط میزی این‌جاست که میزبانش خودش عمومی کرده باشد؛ اتاقِ دو دوست و میزِ
 * بازیِ سریع پخش نمی‌شوند. تماشاچی دستِ هیچ‌کس را نمی‌بیند — نه دستِ خودش
 * (ندارد) و نه دستِ بازیکن‌ها — وگرنه یک نفر کنارِ رفیقش می‌نشست و بازی را
 * برایش می‌برد.
 */
const REFRESH_MS = 5000;

export function Watch({ onBack }: { onBack: () => void }) {
  const tables = useSignal(client.tables);
  const [game, setGame] = useState("");

  // فهرست را سرور خودش نمی‌فرستد: تا این صفحه باز است می‌پرسیم، و همین که
  // بسته شد پرسیدن تمام می‌شود.
  //
  // برگه‌ی پشت هم نمی‌پرسد: فهرستی که کسی نمی‌بیند هر پنج ثانیه بسته‌ی
  // اینترنت و باتری می‌خورَد — و بازار موبایل است. در عوض همین که برگه
  // برگردد یک‌بار می‌پرسیم، وگرنه تا پنج ثانیه فهرستِ کهنه می‌دیدی.
  useEffect(() => {
    const ask = () => { if (document.visibilityState === "visible") client.listTables(game); };
    ask();
    const t = setInterval(ask, REFRESH_MS);
    document.addEventListener("visibilitychange", ask);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", ask); };
  }, [game]);

  return (
    <div className="page stack">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button className={"chip" + (game === "" ? " on" : "")} onClick={() => setGame("")}>همه</button>
        {games.filter((g) => g.online).map((g) => (
          <button key={g.id} className={"chip" + (game === g.id ? " on" : "")} onClick={() => setGame(g.id)}>
            {g.fa}
          </button>
        ))}
      </div>

      {tables.length === 0 ? (
        <div className="plate stack" style={{ textAlign: "center" }}>
          <div>الان میزِ بازی برای تماشا نیست.</div>
          <div className="muted">
            میزها را میزبان‌ها باز می‌کنند: سرِ ساختنِ اتاق، کلیدِ «میزِ عمومی» را که بزنند،
            بازی‌شان همین‌جا می‌آید. اتاقِ کددار و بازیِ سریع هیچ‌وقت پخش نمی‌شوند.
          </div>
        </div>
      ) : (
        tables.map((t) => <TableRow key={t.tableId} t={t} />)
      )}

      <button className="btn ghost" onClick={onBack}>برگرد</button>
    </div>
  );
}

function TableRow({ t }: { t: PublicTable }) {
  const g = games.find((x) => x.id === t.game);
  return (
    <div className="plate stack" style={{ gap: 10 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b style={{ color: "var(--gold-bright)" }}>{g?.fa ?? t.game}</b>
        <span className="muted">
          {t.watchers > 0 ? `${fa(t.watchers)} تماشاچی · ` : ""}
          {sinceText(t.startedAtMs)}
        </span>
      </div>

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {t.seats.map((s) => (
          <span key={s.index} className="row" style={{ gap: 6 }} title={s.name}>
            <img className="seatface" src={avatarSrc(s.avatar)} alt="" width={28} height={28}
                 style={{ width: 28, height: 28, opacity: s.bot ? 0.6 : 1 }} />
            <span className="row" style={{ fontSize: 13, gap: 4 }}>{s.bot && <BotIcon size={14} />}{s.name || "بازیکن"}{placeLabel(s) && <span className="plc-tag">{placeLabel(s)}</span>}</span>
          </span>
        ))}
      </div>

      {t.stake > 0n && <div className="muted">ورودیِ میز: {faNum(t.stake)} سکه</div>}

      <button className="btn teal" onClick={() => client.watchTable(t.tableId)}>تماشا</button>
    </div>
  );
}

/** «۴ دقیقه پیش» — سنِ میز، از ساعتِ سرور، با قالبِ محلیِ مرورگر (Intl، نه دست‌ساز). */
const since = new Intl.RelativeTimeFormat("fa", { numeric: "auto" });
function sinceText(startedAtMs: bigint): string {
  const sec = Math.max(0, Math.round((Date.now() - Number(startedAtMs)) / 1000));
  if (sec < 60) return "تازه شروع شده";
  const min = Math.round(sec / 60);
  if (min < 60) return since.format(-min, "minute");
  return since.format(-Math.round(min / 60), "hour");
}
